import { useSelector } from "react-redux";
import GlobalNotification from "../components/Notifications/GlobalNotification.jsx";
import { SocketProvider } from "../Providers/SocketProvider.js";
import StreamVideoProvider from "../Providers/StreamClientProvider.tsx";
import { selectUserInformations } from "../features/authSlice.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../shared/api/Api.js";
import { LiveSessionProvider } from "../contexts/LiveSessionContext.jsx";
import { showToast } from "../utils/utils.js";

const DashboardLayout = ({ children }) => {
  const user = useSelector(selectUserInformations);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const offlineActivitiesQueue = useRef([]);
  
  // Enhanced offline tracking
  const pingUserOffline = useCallback(() => {
    const url = `${API.Activity.app_activity_markOffLine}/${user.uuid}`;
    const data = JSON.stringify({ userId: user.uuid });

    // Try to use sendBeacon, but fall back to storing the request if it fails
    if (navigator.onLine) {
      const sent = navigator.sendBeacon(url, data);
      if (!sent) {
        // If sendBeacon fails, queue the request for later
        offlineActivitiesQueue.current.push({ url, data });
      }
    } else {
      // If offline, queue the request for later
      offlineActivitiesQueue.current.push({ url, data });
    }
  }, [user.uuid]);

  // Process queued offline activities when back online
  const processOfflineQueue = useCallback(async () => {
    if (offlineActivitiesQueue.current.length === 0) return;
    
    const queue = [...offlineActivitiesQueue.current];
    offlineActivitiesQueue.current = [];
    
    for (const { url, data } of queue) {
      try {
        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: data,
        });
      } catch (error) {
        console.error('Failed to process offline activity:', error);
      }
    }
  }, []);

  // Handle online/offline status changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      processOfflineQueue();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      showToast(
        "You're Offline",
        "Some features may be limited until your connection is restored",
        "warning"
      );
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [processOfflineQueue]);

  // Handle page unload events
  useEffect(() => {
    const handleUnload = () => {
      pingUserOffline();
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [pingUserOffline]);

  return (
    <div className="dashboard-layout bg-[#EBEBEB] w-full h-full">
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-white text-center py-1 z-50">
          You are currently offline. Some features may be limited.
        </div>
      )}
      <SocketProvider token={user.token}>
        <GlobalNotification>
          {/* <StreamVideoProvider> */}
            {/* <LiveSessionProvider> */}
              <main className={!isOnline ? "pt-6" : ""}>{children}</main>
            {/* </LiveSessionProvider> */}
          {/* </StreamVideoProvider> */}
        </GlobalNotification>
      </SocketProvider>
    </div>
  );
};

export default DashboardLayout;
